import React, { useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { FaPhone, FaTimes, FaVideo, FaUser } from 'react-icons/fa';
import styles from './IncomingCallModal.module.css';

/**
 * Kullanıcı jesti olan bir yerden (arama başlatma / kabul) çağrılır; tarayıcı
 * bildirimi izni henüz sorulmadıysa sorar. İzin verilirse, sekme arka
 * plandayken gelen aramalar sistem bildirimi olarak da gösterilir.
 */
export const ensureNotificationPermission = () => {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  } catch (e) {
    // Bildirim desteklenmiyorsa sessizce geç; uygulama içi zil yeterli
  }
};

const IncomingCallModal = ({
  show,
  callerName,
  callerEmail,
  callerImage,
  onAccept,
  onReject,
  callType = 'video'
}) => {
  const answeredRef = useRef(false);

  /**
   * Zil sesi (Web Audio). Her çalımda YENİ osilatörler kurulur: osilatörler
   * tek kullanımlıktır, eski kod aynı osilatörü her 2 saniyede yeniden
   * start() ettiği için ilk çalımdan sonra InvalidStateError fırlatıyor ve
   * ses tamamen kesiliyordu. Klasik çift tonlu (440+480 Hz) zil deseni:
   * iki kısa çalım, ardından sessizlik.
   */
  useEffect(() => {
    if (!show) return;
    let ctx;
    let intervalId;
    let stopped = false;

    const ringBurst = () => {
      if (stopped || !ctx || ctx.state === 'closed') return;
      // Tarayıcı, kullanıcı etkileşimi olmadan sesi askıya almış olabilir
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      [0, 0.55].forEach((offset) => {
        try {
          const osc1 = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          const gain = ctx.createGain();
          osc1.frequency.value = 440;
          osc2.frequency.value = 480;
          osc1.type = 'sine';
          osc2.type = 'sine';
          osc1.connect(gain);
          osc2.connect(gain);
          gain.connect(ctx.destination);

          const t = ctx.currentTime + offset;
          // Yumuşak zarf: çat diye başlayıp bitmesin
          gain.gain.setValueAtTime(0, t);
          gain.gain.linearRampToValueAtTime(0.16, t + 0.04);
          gain.gain.setValueAtTime(0.16, t + 0.3);
          gain.gain.linearRampToValueAtTime(0, t + 0.38);

          osc1.start(t);
          osc2.start(t);
          osc1.stop(t + 0.4);
          osc2.stop(t + 0.4);
        } catch (e) {
          // Ses çalınamazsa görsel zil devam eder
        }
      });
    };

    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      ringBurst();
      intervalId = setInterval(ringBurst, 3000);
    } catch (e) {
      // AudioContext yoksa yalnızca görsel zil
    }

    return () => {
      stopped = true;
      if (intervalId) clearInterval(intervalId);
      try {
        if (ctx && ctx.state !== 'closed') ctx.close();
      } catch (e) {
        // kapatılamadıysa sorun değil
      }
    };
  }, [show]);

  // Zil çalarken sekme başlığı yanıp söner: kullanıcı başka sekmedeyse bile
  // tarayıcı çubuğunda aramayı fark eder.
  useEffect(() => {
    if (!show) return;
    const originalTitle = document.title;
    let flip = false;
    const intervalId = setInterval(() => {
      flip = !flip;
      document.title = flip ? `📞 ${callerName || 'Biri'} arıyor…` : originalTitle;
    }, 1000);
    return () => {
      clearInterval(intervalId);
      document.title = originalTitle;
    };
  }, [show, callerName]);

  // Sekme arka plandaysa ve izin verilmişse sistem bildirimi göster.
  // (İzin, arama başlatma/kabul anlarında ensureNotificationPermission ile
  // istenir; burada asla izin sorulmaz.)
  useEffect(() => {
    if (!show || typeof Notification === 'undefined') return;
    if (!document.hidden || Notification.permission !== 'granted') return;
    let notification;
    try {
      notification = new Notification(
        callType === 'video' ? 'Gelen görüntülü arama' : 'Gelen sesli arama',
        {
          body: `${callerName || 'Bilinmeyen'} sizi arıyor`,
          tag: 'habcare-incoming-call',
          requireInteraction: true
        }
      );
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (e) {
      // Bildirim oluşturulamadıysa uygulama içi zil yeterli
    }
    return () => {
      try {
        if (notification) notification.close();
      } catch (e) {
        // yoksay
      }
    };
  }, [show, callerName, callType]);

  const handleAccept = () => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    ensureNotificationPermission();
    onAccept();
  };

  const handleReject = () => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    onReject();
  };

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      className={styles.incomingCallModal}
      contentClassName={styles.modalContent}
    >
      <Modal.Body className={styles.modalBody}>
        <div className={styles.callerInfo}>
          {/* Caller Avatar with Pulse Animation */}
          <div className={styles.avatarContainer}>
            <div className={styles.pulseRing}></div>
            <div className={styles.pulseRing} style={{ animationDelay: '0.5s' }}></div>
            <div className={styles.avatar}>
              {callerImage ? (
                <img src={callerImage} alt={callerName} />
              ) : (
                <FaUser className={styles.avatarPlaceholder} />
              )}
            </div>
          </div>

          {/* Caller Name — e-posta yalnızca ikincil bilgi olarak, küçük */}
          <h2 className={styles.callerName}>{callerName || 'Bilinmeyen'}</h2>
          {callerEmail && callerEmail !== callerName && (
            <p className={styles.callerEmail}>{callerEmail}</p>
          )}

          {/* Call Type */}
          <p className={styles.callType}>
            {callType === 'video' ? (
              <>
                <FaVideo className={styles.callIcon} />
                Gelen Görüntülü Arama
              </>
            ) : (
              <>
                <FaPhone className={styles.callIcon} />
                Gelen Sesli Arama
              </>
            )}
          </p>

          {/* Ringing Animation */}
          <div className={styles.ringingText}>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          <div className={styles.actionGroup}>
            <button
              className={`${styles.actionButton} ${styles.rejectButton}`}
              onClick={handleReject}
              title="Reddet"
            >
              <FaTimes />
            </button>
            <span className={styles.actionLabel}>Reddet</span>
          </div>

          <div className={styles.actionGroup}>
            <button
              className={`${styles.actionButton} ${styles.acceptButton}`}
              onClick={handleAccept}
              title="Kabul Et"
            >
              <FaPhone />
            </button>
            <span className={styles.actionLabel}>Kabul Et</span>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default IncomingCallModal;
